import { DateTime } from 'luxon'

/**
 * Date utilities for handling date-only fields stored as UTC timestamps.
 *
 * Goal: treat event dates as "calendar dates" (YYYY-MM-DD) and avoid converting them
 * to the viewer's local timezone when displaying.
 */

const SHORT_MONTH_LABELS: Record<number, string> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sept',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
}

/**
 * Extracts a date-only string (YYYY-MM-DD) from:
 * - ISO timestamps like "2025-12-20T00:00:00+00:00"
 * - date-only strings like "2025-12-20"
 */
export function extractDateOnlyYYYYMMDD(value: unknown): string | undefined {
  if (value == null) return undefined
  if (typeof value !== 'string') return undefined
  const s = value.trim()

  // Fast path: already a date-only string.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s

  // Common ISO path: "YYYY-MM-DDT..."
  const beforeT = s.split('T')[0]
  if (/^\d{4}-\d{2}-\d{2}$/.test(beforeT)) return beforeT

  return undefined
}

/**
 * Formats a date-only string (YYYY-MM-DD) into a long date string (e.g. "December 20, 2025")
 * using **UTC** so the displayed calendar day never shifts based on client timezone.
 */
export function formatDateOnlyUtcLong(dateOnly: string, locale: string = 'en-US'): string {
  const [y, m, d] = dateOnly.split('-').map((n) => Number(n))
  // Use a UTC timestamp and format in UTC.
  // Noon UTC avoids any edge-case formatting issues around DST while keeping the day stable.
  const utc = new Date(Date.UTC(y, m - 1, d, 12, 0, 0))
  return new Intl.DateTimeFormat(locale, {
    timeZone: 'UTC',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(utc)
}

/**
 * Formats a date-only string (YYYY-MM-DD) into a compact human date (e.g. "Sept 23, 2026")
 * while preserving the intended UTC calendar day.
 */
export function formatDateOnlyUtcShort(dateOnly: string): string | undefined {
  const dt = DateTime.fromISO(dateOnly, { zone: 'utc' })
  if (!dt.isValid) return undefined

  const month = SHORT_MONTH_LABELS[dt.month]
  return month ? `${month} ${dt.day}, ${dt.year}` : undefined
}

/**
 * Convenience: extract YYYY-MM-DD (if possible) and format as a UTC long date.
 */
export function formatUtcDateLongFromUnknown(value: unknown, locale: string = 'en-US'): string | undefined {
  const dateOnly = extractDateOnlyYYYYMMDD(value)
  return dateOnly ? formatDateOnlyUtcLong(dateOnly, locale) : undefined
}

/**
 * Convenience: extract YYYY-MM-DD (if possible) and format as a compact human date.
 */
export function formatUtcDateShortFromUnknown(value: unknown): string | undefined {
  const dateOnly = extractDateOnlyYYYYMMDD(value)
  return dateOnly ? formatDateOnlyUtcShort(dateOnly) : undefined
}

/**
 * Converts a date string (YYYY-MM-DD or ISO timestamp containing a date) into a JS Date
 * that is safe to use with date pickers in the viewer's local timezone.
 *
 * Why: constructing `new Date("2025-12-20T00:00:00+00:00")` can display as 12/19
 * in negative-offset timezones. Instead we extract `YYYY-MM-DD` and create a local
 * date at noon (midday) so the calendar day remains stable.
 */
export function toLocalDateOnlyFromUnknown(value: string | null | undefined): Date | undefined {
  const dateOnly = extractDateOnlyYYYYMMDD(value)
  if (!dateOnly) return undefined

  const [y, m, d] = dateOnly.split('-').map((n) => Number(n))
  // Local noon avoids DST edge cases while keeping the intended calendar date.
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}
